import React, { useState, useEffect, useContext } from 'react';
import {
  Popover,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Badge,
  IconButton,
  Divider,
  Box,
  CircularProgress,
  Button
} from '@material-ui/core';
import {
  EventAvailable as TaskIcon,
  Schedule as TimeIcon,
  PriorityHigh as HighPriorityIcon,
  Warning as MediumPriorityIcon,
  CheckCircle as LowPriorityIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@material-ui/icons';
import { format, isToday, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { makeStyles } from '@material-ui/core/styles';
import { AuthContext } from '../../context/Auth/AuthContext';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  popoverPaper: {
    padding: theme.spacing(2),
    minWidth: 350,
    maxWidth: 400,
    maxHeight: 500,
    overflow: 'hidden',
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  title: {
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  tasksList: {
    maxHeight: 350,
    overflow: 'auto',
    padding: 0
  },
  taskItem: {
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  taskTitle: {
    fontWeight: 500,
    fontSize: '0.9rem'
  },
  taskTime: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  priorityChip: {
    height: 20,
    fontSize: '0.7rem'
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary
  },
  footer: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2)
  },
  descriptionClamp: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    whiteSpace: 'normal'
  }
}));

const TasksPopover = ({ anchorEl, open, onClose }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  // Buscar tarefas do dia
  const fetchTodayTasks = async () => {
    if (!open) return;
    
    setLoading(true);
    try {
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

      const response = await api.get('/tasks', {
        params: {
          status: 'pending', // Adicionar filtro para buscar apenas tarefas pendentes
          dueDateFrom: startOfDay.toISOString(),
          dueDateTo: endOfDay.toISOString(),
          pageNumber: 1,
          sortBy: 'dueDate',
          sortOrder: 'ASC'
        }
      });

      const todayTasks = response.data.tasks || [];
      setTasks(todayTasks);
      
      // Contar tarefas pendentes com lembretes ativos
      const pendingWithReminders = todayTasks.filter(task => 
        task.status === 'pending' && 
        task.reminderEnabled && 
        task.reminderDate &&
        new Date(task.reminderDate) <= new Date()
      );
            
      // Debug: mostrar tarefas sendo contadas
      if (pendingWithReminders.length > 0) {
        pendingWithReminders.forEach(task => {
          console.log(`[TASKS_POPOVER] Tarefa ${task.id}: "${task.title}"`, {
            reminderTime: new Date(task.reminderDate).toLocaleTimeString(),
            reminderSent: task.reminderSent,
            dueDate: new Date(task.dueDate).toLocaleDateString()
          });
        });
      }
      
      setNotificationCount(pendingWithReminders.length);

    } catch (error) {
      console.error('Erro ao buscar tarefas:', error);
      toast.error('Erro ao carregar tarefas do dia');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodayTasks();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const interval = setInterval(() => {
      fetchTodayTasks();
    }, 60000);

    return () => clearInterval(interval);
  }, [open]);

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <HighPriorityIcon style={{ color: '#f44336', fontSize: 16 }} />;
      case 'medium':
        return <MediumPriorityIcon style={{ color: '#ff9800', fontSize: 16 }} />;
      case 'low':
        return <LowPriorityIcon style={{ color: '#4caf50', fontSize: 16 }} />;
      default:
        return <TaskIcon style={{ color: '#9e9e9e', fontSize: 16 }} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#f44336';
      case 'medium': return '#ff9800';
      case 'low': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'Alta';
      case 'medium': return 'Média';
      case 'low': return 'Baixa';
      default: return 'Normal';
    }
  };

  const formatTaskTime = (dueDate) => {
    try {
      const date = typeof dueDate === 'string' ? parseISO(dueDate) : dueDate;
      return format(date, "HH:mm", { locale: ptBR });
    } catch (error) {
      return '--:--';
    }
  };

  const formatReminderTime = (reminderDate) => {
    if (!reminderDate) return null;
    try {
      const date = typeof reminderDate === 'string' ? parseISO(reminderDate) : reminderDate;
      return format(date, "HH:mm", { locale: ptBR });
    } catch (error) {
      return null;
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        className: classes.popoverPaper
      }}
    >
      <div className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          <TaskIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Tarefas de Hoje
        </Typography>
        <div>
          <IconButton size="small" onClick={fetchTodayTasks} title="Atualizar">
            <RefreshIcon />
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={24} />
        </div>
      ) : (
        <>
          {tasks.length === 0 ? (
            <div className={classes.emptyState}>
              <TaskIcon style={{ fontSize: 48, color: '#ccc', marginBottom: 8 }} />
              <Typography variant="body2">
                Nenhuma tarefa para hoje
              </Typography>
            </div>
          ) : (
            <List className={classes.tasksList}>
              {tasks.map((task) => (
                <ListItem key={task.id} className={classes.taskItem}>
                  <ListItemIcon>
                    {getPriorityIcon(task.priority)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography className={classes.taskTitle}>
                          {task.title}
                        </Typography>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <Chip
                            size="small"
                            label={getPriorityLabel(task.priority)}
                            style={{
                              backgroundColor: getPriorityColor(task.priority),
                              color: 'white',
                              fontSize: '0.7rem',
                              height: 20
                            }}
                          />
                          {task.status === 'completed' && (
                            <Chip
                              size="small"
                              label="Concluída"
                              style={{
                                backgroundColor: '#4caf50',
                                color: 'white',
                                fontSize: '0.7rem',
                                height: 20
                              }}
                            />
                          )}
                        </div>
                      </div>
                    }
                    secondary={
                      <div>
                        <div className={classes.taskTime}>
                          <TimeIcon style={{ fontSize: 14 }} />
                          Vencimento: {formatTaskTime(task.dueDate)}
                          {task.reminderEnabled && task.reminderDate && (
                            <span style={{ marginLeft: 8, color: '#1976d2' }}>
                              🔔 {formatReminderTime(task.reminderDate)}
                            </span>
                          )}
                        </div>
                        {task.description && (
                          <Typography 
                            variant="caption" 
                            style={{ display: 'block', marginTop: 4 }}
                            className={classes.descriptionClamp}
                            title={task.description}
                          >
                            {task.description}
                          </Typography>
                        )}
                      </div>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          <div className={classes.footer}>
            <Button
              component={RouterLink}
              to="/tasks"
              fullWidth
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={onClose}
            >
              Ver Todas as Tarefas
            </Button>
          </div>
        </>
      )}
    </Popover>
  );
};

export default TasksPopover;
